import React, { useEffect, useState, useCallback } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import api from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Wallet, Trash2 } from 'lucide-react';
import { EXPENSE_CATEGORIES } from '@/lib/constants';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function Expenses() {
  const { user } = useAuth();
  const canWrite = ['admin', 'accountant'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), amount: '', category: 'equipment', description: '', receipt_url: '' });
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/expenses', { params: filter === 'all' ? {} : { category: filter } });
      setItems(data);
    } catch {
      toast.error('Failed to load expenses');
    }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      await api.post('/expenses', { ...form, amount: Number(form.amount) });
      toast.success('Expense logged');
      setOpen(false);
      setForm({ date: new Date().toISOString().slice(0, 10), amount: '', category: 'equipment', description: '', receipt_url: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this expense?')) return;
    try { await api.delete(`/expenses/${id}`); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };

  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <AppShell title="Expenses" actions={canWrite ? (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button data-testid="expenses-add-button"><Plus className="h-4 w-4 mr-1" />Log expense</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Log expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" /></div>
              <div><Label>Amount</Label><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" /></div>
            </div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc" /></div>
            <div><Label>Receipt URL (optional)</Label><Input value={form.receipt_url} onChange={(e) => setForm({ ...form, receipt_url: e.target.value })} data-testid="expense-receipt" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} disabled={!form.amount} data-testid="expense-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    ) : null}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="expenses-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="ml-auto text-sm text-muted-foreground">Total: <span className="text-foreground font-semibold tabular-nums">${total.toFixed(2)}</span></div>
        </div>

        <Card data-testid="expenses-page">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="bg-secondary/60"><tr><th className="text-left p-3">Date</th><th className="text-left p-3">Category</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Description</th><th className="text-left p-3">Logged by</th><th></th></tr></thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.expense_id} className="border-t hover:bg-accent/40">
                    <td className="p-3">{e.date}</td>
                    <td className="p-3">{e.category}</td>
                    <td className="p-3 tabular-nums">${Number(e.amount).toFixed(2)}</td>
                    <td className="p-3">{e.description}</td>
                    <td className="p-3 text-muted-foreground">{e.logged_by_name}</td>
                    <td className="p-3">{canWrite && <Button variant="ghost" size="icon" onClick={() => remove(e.expense_id)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" /></Button>}</td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground" data-testid="empty-state"><Wallet className="h-6 w-6 mx-auto mb-2 opacity-60" />No expenses logged.</td></tr>}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
